const EXPIRED_STATUSES = new Set(['expired', 'canceled', 'cancelled'])
const NOT_EXPIRED_STATUSES = new Set(['active', 'pending'])

/**
 * Normalizes API `isActive` values to a boolean when recognizable.
 * Returns undefined when absent or unrecognized so callers can fall back.
 *
 * @param {*} isActive
 * @returns {boolean|undefined}
 */
export const coerceIsActive = isActive => {
  if (typeof isActive === 'boolean') return isActive

  if (typeof isActive === 'number') {
    if (isActive === 1) return true
    if (isActive === 0) return false
    return undefined
  }

  if (typeof isActive === 'string') {
    const normalized = isActive.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1') return true
    if (normalized === 'false' || normalized === '0') return false
  }

  return undefined
}

/**
 * Determines whether a license/asset should be treated as expired.
 * Prefers `isActive` over NetSuite `status`, which can stay "Expired"
 * after an extension while `isActive` is already true again.
 *
 * Priority:
 * 1. recognizable `isActive` (boolean, "true"/"false", 1/0)
 * 2. `status` when `isActive` is absent/unrecognized (legacy payloads)
 * 3. `expirationDate` vs now
 */
export const resolveIsExpired = ({ isActive, status, expirationDate }) => {
  const active = coerceIsActive(isActive)
  if (typeof active === 'boolean') {
    return !active
  }

  if (typeof status === 'string') {
    const normalized = status.toLowerCase()
    if (EXPIRED_STATUSES.has(normalized)) {
      return true
    }
    if (NOT_EXPIRED_STATUSES.has(normalized)) {
      return false
    }
  }

  if (expirationDate == null) return false

  const expires = Date.parse(expirationDate)
  return Number.isFinite(expires) && Date.now() > expires
}

export const findExpiredAdminLicenses = licenses =>
  licenses.filter(license => {
    const isAdminLicense = license.claims.find(
      claim => claim.toLowerCase() === 'admin',
    )

    if (isAdminLicense) return license
  })

export const findExpiredNonAdminLicenses = licenses =>
  licenses.filter(license => {
    const isAdminLicense = license.claims.find(
      claim => claim.toLowerCase() === 'admin',
    )
    const isNonAdminLicense = license.claims.find(
      claim => claim.toLowerCase() === 'teacher',
    )
    if (!isAdminLicense && isNonAdminLicense) return license
  })

export const getExpiredLicensesWithClaims = sites => {
  const expiredLicenses = (sites || []).reduce((expLicenseAcc, site) => {
    const { claims } = site
    ;(site?.licenses || []).forEach(license => {
      if (
        license.isExpired &&
        !license.cancelledDate &&
        license.product?.programCodes
      ) {
        const {
          id,
          expirationDate,
          siteName,
          product: { programCodes, type },
        } = license
        const licenseWithClaims = {
          id,
          expirationDate,
          siteName,
          product: {
            programCodes,
            type,
          },
          claims,
        }
        expLicenseAcc.push(licenseWithClaims)
      }
    })
    return expLicenseAcc
  }, [])
  return expiredLicenses
}
